// adPixels.js — TikTok Pixel + Meta Pixel for web ad campaigns.
//
// Consent-gated: nothing loads until the cookie banner records
// `accepted_all` (window.__fws_consent — see CookieBanner.js), matching the
// PostHog gating policy. Inert when the REACT_APP_*_PIXEL_ID env vars are
// blank/placeholder, so preview & unconfigured builds send zero requests.
// Pixel IDs are public client-side identifiers (visible in any browser bundle),
// so committed defaults are safe. Env vars still override when set at build time.
const TIKTOK_ID = (process.env.REACT_APP_TIKTOK_PIXEL_ID || 'DA2VNC3C77UFIU51B8G0').trim();
const META_ID = (process.env.REACT_APP_META_PIXEL_ID || '1454962796778080').trim();

const badId = (id) => !id || /^(your|change|replace|placeholder|undefined|null|xxx|000)/i.test(id);
const hasTikTok = !badId(TIKTOK_ID);
const hasMeta = !badId(META_ID);

let loaded = false;

const consentGranted = () =>
  typeof window !== 'undefined' && window.__fws_consent?.choice === 'accepted_all';

function loadMeta() {
  if (!hasMeta || window.fbq) return;
  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', META_ID);
  window.fbq('track', 'PageView');
}

function loadTikTok() {
  if (!hasTikTok || window.ttq) return;
  window.TiktokAnalyticsObject = 'ttq';
  const ttq = (window.ttq = window.ttq || []);
  ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
  ttq.setAndDefer = (target, method) => {
    target[method] = (...args) => target.push([method, ...args]);
  };
  ttq.methods.forEach((m) => ttq.setAndDefer(ttq, m));
  ttq.load = (id) => {
    ttq._i = ttq._i || {}; ttq._i[id] = [];
    ttq._i[id]._u = 'https://analytics.tiktok.com/i18n/pixel/events.js';
    ttq._t = ttq._t || {}; ttq._t[id] = Date.now();
    const s = document.createElement('script');
    s.async = true;
    s.src = `${ttq._i[id]._u}?sdkid=${encodeURIComponent(id)}&lib=ttq`;
    document.head.appendChild(s);
  };
  ttq.load(TIKTOK_ID);
  ttq.page();
}

// Idempotent — safe to call on mount AND on every consent change.
export function initAdPixels() {
  if (loaded || (!hasTikTok && !hasMeta) || !consentGranted()) return;
  loaded = true;
  loadMeta();
  loadTikTok();
  applyIdentity();
}

// ── Advanced matching ────────────────────────────────────────────────────────
// Both pixel libraries SHA-256 hash PII client-side before sending. Improves
// attribution for users who block third-party cookies / decline ATT on mobile.
let pendingIdentity = null;

function applyIdentity() {
  if (!pendingIdentity || !loaded) return;
  const { id, email } = pendingIdentity;
  const em = email ? String(email).trim().toLowerCase() : null;
  try {
    window.ttq?.identify({ ...(em ? { email: em } : {}), ...(id ? { external_id: String(id) } : {}) });
  } catch (_e) {}
  try {
    if (hasMeta && window.fbq) {
      window.fbq('init', META_ID, { ...(em ? { em } : {}), ...(id ? { external_id: String(id) } : {}) });
    }
  } catch (_e) {}
}

export function adIdentify({ id, email } = {}) {
  if (!id && !email) return;
  pendingIdentity = { id, email };
  applyIdentity();
}

export function adTrackPageView() {
  if (!loaded) return;
  try { window.fbq?.('track', 'PageView'); } catch (_e) {}
  try { window.ttq?.page(); } catch (_e) {}
}

export function adTrackRegistration(method = 'email') {
  if (!loaded) return;
  try { window.fbq?.('track', 'CompleteRegistration', { status: true, method }); } catch (_e) {}
  try { window.ttq?.track('CompleteRegistration', { status: true, method }); } catch (_e) {}
}

export function adTrackPurchase({ value, currency = 'USD', orderId } = {}) {
  if (!loaded || !Number.isFinite(Number(value))) return;
  const data = { value: Number(value), currency, ...(orderId ? { content_ids: [String(orderId)] } : {}) };
  try { window.fbq?.('track', 'Purchase', data, orderId ? { eventID: String(orderId) } : undefined); } catch (_e) {}
  try { window.ttq?.track('Purchase', { ...data, ...(orderId ? { event_id: String(orderId) } : {}) }); } catch (_e) {}
}
